import { css } from "@emotion/react";
import styled from "@emotion/styled";
import React from "react";
import { useId } from "@radix-ui/react-id";

import { chooseThemeVariant } from "../ThemeContext";
import { SmallText } from "../Typography";

interface TextFieldProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: React.ReactNode;
  addon?: React.ReactNode;
}

const Root = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${chooseThemeVariant({ translucent: "4px", default: "8px" })};
  color: ${chooseThemeVariant({
    translucent: "rgba(255, 255, 255, 0.8)",
    default: "#000",
  })};
`;

const InputWrapper = styled.div`
  display: flex;
  overflow: hidden;
  background: ${chooseThemeVariant({
    translucent: "rgba(255, 255, 255, 0.08)",
    default:
      "linear-gradient(267.45deg, #05D5FF -34.23%, #5533FF 99.39%) border-box",
  })};
  border: 1px solid
    ${chooseThemeVariant({
      translucent: "rgba(255, 255, 255, 0.3)",
      default: "#CACED4",
    })};
  border-radius: 4px;
  transition: 300ms ease-in-out;

  &:hover,
  &:focus-within {
    border-color: ${chooseThemeVariant({
      translucent: "rgba(255, 255, 255, 0.8)",
      default: "transparent",
    })};
  }
`;

const Input = styled.input<{ hasLabel: boolean; hasAddon: boolean }>`
  display: block;
  background: ${chooseThemeVariant({
    translucent: "transparent",
    default: "#fff",
  })};
  color: ${chooseThemeVariant({ translucent: "#fff", default: "#414348" })};
  border: 0;
  padding: 10px ${(props) => (props.hasLabel ? "16px" : "12px")};
  ${(props) =>
    props.hasAddon
      ? css`
          padding-right: 4px;
        `
      : null};
  width: 100%;
  height: ${(props) =>
    chooseThemeVariant({
      translucent: props.hasLabel ? "46px" : "42px",
      default: "42px",
    })(props)};
  font-size: 14px;
  line-height: 150%;

  &:focus {
    outline: none;
  }

  &::placeholder {
    color: ${chooseThemeVariant({
      translucent: "rgba(255, 255, 255, 0.5)",
      default: "#767B81",
    })};
  }
`;

const Addon = styled.div<{ hasLabel: boolean }>`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  padding-right: ${(props) =>
    chooseThemeVariant({
      translucent: props.hasLabel ? "16px" : "12px",
      default: "12px",
    })(props)};
  background: ${chooseThemeVariant({
    translucent: "transparent",
    default: "#fff",
  })};
`;

const Label = styled(SmallText)``.withComponent("label");

const TextField = ({
  label,
  className,
  addon,
  ...inputProps
}: TextFieldProps) => {
  const id = useId();

  return (
    <Root>
      {label && <Label htmlFor={id}>{label}</Label>}

      <InputWrapper className={className}>
        <Input id={id} hasLabel={!!label} hasAddon={!!addon} {...inputProps} />

        {addon && <Addon hasLabel={!!label}>{addon}</Addon>}
      </InputWrapper>
    </Root>
  );
};

export default TextField;
